import {
  useEffect,
  useMemo,
  useReducer,
  useCallback,
  useState
} from 'react';
import moment from 'moment';

import CartonGuideApi from '../api/CartonGuideApi';
import normalizeGuides from '../utils/normalizeGuides';
import App from '../../../app';

const reducer = (state, action) => {
  switch (action.type) {
    case 'store':
      return action.payload;

    case 'update':
      if (Array.isArray(action.payload)) {
        // Merge each guide into state
        const updatedState = { ...state };
        action.payload.forEach(guide => {
          updatedState[guide.id] = {
            ...state[guide.id],
            ...guide
          };
        });
        return updatedState;
      } else {
        // Single guide
        return {
          ...state,
          [action.payload.id]: {
            ...state[action.payload.id],
            ...action.payload
          }
        };
      }
      
    default:
      return state;
  }
};

export default function useCartonGuides(cartonId, onScanSuccess, onScanError) {
  const [state, dispatch] = useReducer(reducer, {});
  const [error, setError] = useState();

  useEffect(() => {
    (async () => {
      try {
        const guides = await CartonGuideApi.all(cartonId);
        dispatch({
          type: 'store',
          payload: normalizeGuides(guides)
        });
      } catch (e) {
        console.error(e);
      }
    })();
  }, [cartonId]);

  const guides = useMemo(
    () => Object.values(state)
      .sort((a, b) => moment(b.lastScanDatetime).diff(moment(a.lastScanDatetime))),
    [state]
  );

  const scans = useMemo(
    () => Object.values(state).reduce(
      (sum, guide) => sum + (guide.scannedPackagesAmount >= guide.packagesAmount ? 1 : 0),
      0
    ),
    [state]
  );

  const scansFree = useMemo(
    () => Object.values(state).reduce(
      (sum, guide) => sum + (guide.scannedPackagesAmount >= guide.packagesAmount
        && guide.potentialClassification === 'L'
        ? 1 : 0),
      0
    ),
    [state]
  );

  const guidesFree = useMemo(
      () => Object.values(state).reduce(
        (sum, guide) => sum + (guide.potentialClassification === 'L' ? 1 : 0),
        0
      ),
      [state]
  );

  const scansExcept = useMemo(
    () => Object.values(state).reduce(
      (sum, guide) => sum + (guide.scannedPackagesAmount >= guide.packagesAmount
        && guide.potentialClassification != 'L'
        ? 1 : 0),
      0
    ),
    [state]
  );

  const guidesExcept = useMemo(
      () => Object.values(state).reduce(
        (sum, guide) => sum + (guide.potentialClassification != 'L' ? 1 : 0),
        0
      ),
      [state]
  );

  const onScan = useCallback(
    async (guideNumber) => {
      try {
        const guides = await CartonGuideApi.scan(cartonId, guideNumber);
        const guide = guides[0]
        dispatch({ type: 'update', payload: guides });

        if (guide.potentialClassification === 'F') {
          throw new Error(gettext('guide with physical classification'));
        } else if (guide.potentialClassification === 'R' || guide.potentialClassification === 'R-NG' || guide.potentialClassification === 'R-IN' || guide.potentialClassification === 'R-IS') {
          throw new Error(gettext('guide with retained classification'));
        }

        if (onScanSuccess) {
          onScanSuccess();
        }
      } catch (requestError) {
        setError(requestError.message);
        if (onScanError) {
          onScanError();
        }
      } finally {
        App.utils.hideLoadingButtons();
      }
    },
    [cartonId, onScanError, onScanSuccess]
  );

  const clearError = () => setError(undefined);

  return {
    data: {
      guides,
      scans,
      guidesFree,
      scansFree,
      guidesExcept,
      scansExcept,
      error
    },
    actions: {
      onScan,
      clearError
    }
  };
}
